/* ============================================================
 * Editorial primitives — 2026 design system
 * Shared across /services, /services/*, and any new editorial page.
 * Mirrors the locals defined in src/pages/Index.tsx so the
 * homepage rebuild can adopt these later without behavior change.
 * ============================================================ */

import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import ContactModal from "@/components/ContactModal";

// — Eyebrow ————————————————————————————————————————————————

export const Eyebrow = ({ children }: { children: React.ReactNode }) => (
  <span className="eyebrow">{children}</span>
);

// — SectionShell ————————————————————————————————————————————

export const SectionShell = ({
  children,
  className = "",
  id,
}: {
  children: React.ReactNode;
  className?: string;
  id?: string;
}) => (
  <section id={id} className={`relative py-16 md:py-20 px-6 ${className}`}>
    <div className="max-w-6xl mx-auto">{children}</div>
  </section>
);

// — PhotoPanel ————————————————————————————————————————————

// — Buttons (CTA primary / secondary) ————————————————————————

const PRIMARY_CLS =
  "inline-flex items-center justify-center gap-2 px-6 h-12 rounded-sm bg-[hsl(var(--accent-brick))] text-[hsl(var(--bg-canvas))] hover:bg-[hsl(var(--accent-brick-hover))] transition-colors duration-150 ease-out text-[14px] font-medium tracking-[-0.01em]";

const SECONDARY_CLS =
  "inline-flex items-center justify-center gap-2 px-6 h-12 rounded-sm border border-[hsl(var(--border-hairline))] text-[hsl(var(--fg-cream))] hover:border-[hsl(var(--accent-brick))] transition-colors duration-150 ease-out text-[14px] font-medium tracking-[-0.01em]";

/**
 * Primary brick CTA.
 * - Default: opens the ContactModal (book diagnostic flow).
 * - With `onClick`: renders a plain <button> for in-page actions
 *   (e.g. scroll-to-pricing, scroll-to-booking).
 */
export const PrimaryCTA = ({
  label,
  modalTitle,
  onClick,
}: {
  label: string;
  modalTitle?: string;
  onClick?: () => void;
}) => {
  if (onClick) {
    return (
      <button type="button" onClick={onClick} className={PRIMARY_CLS}>
        {label}
        <ArrowRight className="h-4 w-4" />
      </button>
    );
  }
  return (
    <ContactModal
      triggerLabel={label}
      triggerClassName={PRIMARY_CLS}
      modalTitle={modalTitle ?? "Stop experimenting. Start compounding."}
    />
  );
};

/** Secondary outline CTA — button form for in-page actions (scroll, modal open). */
export const SecondaryCTA = ({
  label,
  onClick,
}: {
  label: string;
  onClick: () => void;
}) => (
  <button type="button" onClick={onClick} className={SECONDARY_CLS}>
    {label}
    <ArrowRight className="h-4 w-4" />
  </button>
);

/** Secondary outline CTA. Internal SPA link or external href. */
export const SecondaryLinkCTA = ({
  to,
  label,
  external = false,
}: {
  to: string;
  label: string;
  external?: boolean;
}) =>
  external ? (
    <a href={to} target="_blank" rel="noopener noreferrer" className={SECONDARY_CLS}>
      {label}
      <ArrowUpRight className="h-4 w-4" />
    </a>
  ) : (
    <Link to={to} className={SECONDARY_CLS}>
      {label}
      <ArrowRight className="h-4 w-4" />
    </Link>
  );

// — Bullet list ————————————————————————————————————————————

export const BulletList = ({ items }: { items: string[] }) => (
  <ul className="space-y-3">
    {items.map((b) => (
      <li
        key={b}
        className="flex gap-3 text-[15px] text-[hsl(var(--fg-cream))]/85 leading-relaxed"
      >
        <span className="text-[hsl(var(--accent-brick))] mt-1.5 flex-shrink-0">▸</span>
        <span>{b}</span>
      </li>
    ))}
  </ul>
);
